import {
  instanceOfPayrollRunEmployeeBreakdown,
  PayrollRun
} from "@wingspanhq/wingspan-api-sdk";
import sumBy from "lodash/sumBy";

import { parsePayrollAmount } from "../utils";

export type PayrollRunBreakdownAmounts = {
  grossAmount: number;
  payerTaxes: number;
  benefits: number;
  paystubsCount: number;
  /** `undefined` until the run has been read with the breakdown expanded. */
  liabilityAmount: number | undefined;
};

/**
 * Reads the run-level rollups off `PayrollRun.breakdown`.
 *
 * The API only populates `breakdown` when the read passes
 * `expand: [PayrollRunExpand.BREAKDOWN]` — `listPayrollRuns` has no `expand`
 * parameter, so list screens fetch it per row/card (see
 * `useQueryPayrollRunBreakdown`). `breakdown` is a union of the
 * contractor/vendor and employee shapes; these rollups (gross pay, payer taxes,
 * pay statements) only exist on the employee shape, so a contractor/vendor run
 * yields zeros here — see `selectorPayrollRunBreakdownCount` for the count that
 * spans both. `benefits` has no v3 breakdown field yet and stays 0.
 */
export const selectorPayrollRunBreakdownAmounts = (
  payroll: PayrollRun | undefined
): PayrollRunBreakdownAmounts => {
  const breakdown =
    payroll?.breakdown &&
    instanceOfPayrollRunEmployeeBreakdown(payroll.breakdown)
      ? payroll.breakdown
      : undefined;

  // `grossLiabilityAmount` mirrors the run's top-level `totalAmount`; fall back
  // to `totalAmount` when the breakdown was not expanded.
  const liabilityAmount =
    breakdown?.grossLiabilityAmount ?? payroll?.totalAmount;

  return {
    grossAmount: parsePayrollAmount(breakdown?.grossPay),
    payerTaxes: sumBy(breakdown?.payerTaxes ?? [], entry =>
      parsePayrollAmount(entry.amount)
    ),
    benefits: 0,
    paystubsCount: breakdown?.payStatementsCount ?? 0,
    liabilityAmount:
      liabilityAmount === undefined
        ? undefined
        : parsePayrollAmount(liabilityAmount)
  };
};
